import { forwardRef, useState, useEffect } from 'react';
import { empresaAPI, getUploadUrl } from '@/lib/api';
import QRCode from 'react-qr-code';
import CondicionesGeneralesPDF from '@/components/CondicionesGeneralesPDF';
import { calcularTotales } from '@/lib/totales';
import { S, statusLabels } from '@/components/ordenpdf/styles';
import { BarcodeImage, PdfPhotoCell } from '@/components/ordenpdf/PdfPrimitives';
import { buildPhotoPages } from '@/components/ordenpdf/pdf-fotos';

const OrdenPDF = forwardRef(function OrdenPDF(
  { orden, cliente, materiales = [], mode = 'full', modoB2B = false, includeFotos = false, onlyMain = false, onlyFotos = false, skipCondiciones = false },
  ref
) {
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    empresaAPI.obtener().then(r => setEmpresa(r.data)).catch(() => {});
  }, []);

  const isBlank  = mode === 'blank_no_prices';
  const isNoPrices = mode === 'no_prices' || isBlank;
  const isFull   = mode === 'full';

  // Estado visible: si garantía no procede, mostrarlo explícitamente
  const estadoVisible = (() => {
    if (isBlank) return '—';
    if (orden?.es_garantia && orden?.garantia_resultado === 'no_procede') {
      return 'FUERA DE GARANTIA';
    }
    return statusLabels[orden?.estado] || orden?.estado || '—';
  })();

  const val = (v, fb = '—') => {
    if (isBlank) return '_____';
    return (v == null || v === '') ? fb : v;
  };

  const boolLabel = (v) => {
    if (isBlank) return '_____';
    if (v == null) return 'PEND.';
    return v ? 'Sí' : 'No';
  };

  const fmtDate = (d) => {
    if (!d) return isBlank ? '__/__/____' : '—';
    return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const fmtDT = (d) => {
    if (!d) return isBlank ? '__/__/____' : '—';
    return new Date(d).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const totales = calcularTotales(materiales, {
    ivaDefault: empresa?.iva_por_defecto ?? 21,
  });

  const logoSrc = (() => {
    const raw = empresa?.logo?.url || empresa?.logo_url;
    if (!raw) return null;
    return raw.startsWith('http') ? raw : getUploadUrl(raw);
  })();

  /**
   * Devuelve un nombre legible para mostrar en el PDF, sin exponer emails
   * corporativos/personales. Si recibe un email, devuelve solo la parte local
   * (antes de @) en mayúsculas y reemplazando puntos/guiones por espacios.
   */
  const safeName = (raw, fallback = '—') => {
    if (!raw || typeof raw !== 'string') return fallback;
    const v = raw.trim();
    if (!v) return fallback;
    if (v.includes('@')) {
      const local = v.split('@')[0] || '';
      const pretty = local.replace(/[._-]+/g, ' ').trim();
      return pretty ? pretty.charAt(0).toUpperCase() + pretty.slice(1) : fallback;
    }
    return v;
  };

  const getCreador = () => {
    // Preferir el nombre persistido en la creación (OTs nuevas) o, si no, el
    // primer registro del historial sanitizado.
    const persisted = orden?.creado_por_nombre;
    if (persisted) return safeName(persisted, 'Sistema');
    const raw = (orden?.historial_estados || [])[0]?.usuario;
    return safeName(raw, 'Sistema');
  };

  const getTecnicoNombre = () =>
    safeName(orden?.tecnico_nombre || orden?.tecnico_asignado, '—');

  // ── Fotos para el Anexo ─────────────────────────────────────────────────
  // Agrupadas por fase (Recepción → Antes → Después → Otras) y paginadas
  // a 8 fotos por página A4. Extracción en /components/ordenpdf/pdf-fotos.js
  const { fotosPorFase, allFotos, photoPages } = buildPhotoPages(orden, {
    includeFotos,
    isBlank,
  });

  const totalPages = 1 + photoPages.length;

  return (
    <div ref={ref}>

      {/* ══════════════════════════════════════════════════════════
          PÁGINA PRINCIPAL
      ══════════════════════════════════════════════════════════ */}
      {!onlyFotos && (
      <div style={{
        ...S.page,
        ...(onlyMain ? { pageBreakAfter: 'always', breakAfter: 'page' } : {}),
      }}>

        {/* CABECERA */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm' }}>
          <div>
            {logoSrc ? (
              <img src={logoSrc} alt="Logo" style={{ height: '44px', maxWidth: '150px', objectFit: 'contain' }} />
            ) : (
              <p style={{ fontSize: '18px', fontWeight: '800', margin: 0 }}>{empresa?.nombre || 'Mi Empresa'}</p>
            )}
            <p style={{ fontSize: '7.5px', color: '#666', margin: '1.5mm 0 0' }}>
              {empresa?.direccion}{empresa?.codigo_postal && `, ${empresa.codigo_postal}`}{empresa?.ciudad && ` ${empresa.ciudad}`}
            </p>
            <p style={{ fontSize: '7.5px', color: '#666', margin: '0.5mm 0 0' }}>
              {empresa?.cif && `CIF: ${empresa.cif}`}{empresa?.telefono && ` · Tel: ${empresa.telefono}`}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '20px', fontWeight: '800', margin: 0, letterSpacing: '-0.5px', fontFamily: 'monospace' }}>
              {isBlank ? 'OT-________' : (orden?.numero_orden || 'OT-000000')}
            </p>
            <p style={{ fontSize: '9px', color: '#555', margin: '1mm 0', textTransform: 'uppercase', fontWeight: '600' }}>
              {estadoVisible}
            </p>
            <p style={{ fontSize: '7.5px', color: '#999', margin: 0 }}>{fmtDate(orden?.created_at)}</p>
            {orden?.numero_autorizacion && !isBlank && (
              <p style={{ fontSize: '8px', color: '#444', margin: '1mm 0 0', fontFamily: 'monospace' }}>
                Aut: {orden.numero_autorizacion}
              </p>
            )}
          </div>
        </div>

        <div style={S.hrDark} />

        {/* BLOQUE 1: CLIENTE + DISPOSITIVO (2 columnas) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8mm', marginBottom: '3mm' }}>

          {/* Columna izquierda: Cliente */}
          <div>
            <span style={S.label}>Cliente</span>
            <p style={{ fontSize: '11px', fontWeight: '700', margin: '0 0 1.5mm' }}>
              {isBlank
                ? '___________________________'
                : `${cliente?.nombre || ''} ${cliente?.apellidos || ''}`.trim() || '—'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm' }}>
              <div>
                <span style={S.label}>DNI</span>
                <span style={S.value}>{val(cliente?.dni)}</span>
              </div>
              <div>
                <span style={S.label}>Teléfono</span>
                <span style={S.value}>{val(cliente?.telefono)}</span>
              </div>
            </div>
            <p style={{ fontSize: '7.5px', margin: '1mm 0', color: '#555' }}>{val(cliente?.email, '')}</p>
            <p style={{ fontSize: '7.5px', margin: 0, color: '#888' }}>
              {isBlank
                ? '________________________________'
                : `${cliente?.direccion || ''}${cliente?.codigo_postal ? `, ${cliente.codigo_postal}` : ''}${cliente?.ciudad ? ` ${cliente.ciudad}` : ''}` || '—'}
            </p>
          </div>

          {/* Columna derecha: Dispositivo */}
          <div>
            <span style={S.label}>Dispositivo</span>
            <p style={{ fontSize: '11px', fontWeight: '700', margin: '0 0 1.5mm' }}>
              {val(orden?.dispositivo?.modelo || orden?.dispositivo_modelo, 'Sin especificar')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm' }}>
              <div>
                <span style={S.label}>IMEI / SN</span>
                <span style={{ ...S.value, fontFamily: 'monospace', fontSize: '8px' }}>
                  {val(orden?.dispositivo?.imei || orden?.dispositivo_imei, 'PENDIENTE')}
                  {orden?.dispositivo?.imei2 ? ` / ${orden.dispositivo.imei2}` : ''}
                </span>
              </div>
              <div>
                <span style={S.label}>Color</span>
                <span style={S.value}>{val(orden?.dispositivo?.color || orden?.dispositivo_color)}</span>
              </div>
            </div>
            <div style={{ marginTop: '2mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm' }}>
              <div>
                <span style={S.label}>Técnico</span>
                <span style={S.value}>{getTecnicoNombre()}</span>
              </div>
              <div>
                <span style={S.label}>Tipo</span>
                <span style={S.value}>{val(orden?.tipo_servicio || orden?.origen || (modoB2B ? 'B2B' : 'B2C'))}</span>
              </div>
            </div>
          </div>
        </div>

        {/* FECHAS (5 columnas) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '3mm', marginBottom: '3mm', paddingTop: '2mm', borderTop: '1px solid #eee' }}>
          <div><span style={S.label}>Creación</span><span style={{ ...S.value, fontSize: '7.5px' }}>{fmtDT(orden?.created_at)}</span></div>
          <div><span style={S.label}>Recepción</span><span style={{ ...S.value, fontSize: '7.5px' }}>{fmtDT(orden?.fecha_recibida_centro)}</span></div>
          <div><span style={S.label}>Inicio rep.</span><span style={{ ...S.value, fontSize: '7.5px' }}>{fmtDT(orden?.fecha_inicio_reparacion)}</span></div>
          <div><span style={S.label}>Fin rep.</span><span style={{ ...S.value, fontSize: '7.5px' }}>{fmtDT(orden?.fecha_fin_reparacion)}</span></div>
          <div><span style={S.label}>Envío</span><span style={{ ...S.value, fontSize: '7.5px' }}>{fmtDT(orden?.fecha_enviado)}</span></div>
        </div>

        {/* REFERENCIAS (4 columnas) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3mm', marginBottom: '3mm', paddingTop: '2mm', borderTop: '1px solid #eee' }}>
          <div>
            <span style={S.label}>OT ID</span>
            <span style={{ ...S.value, fontFamily: 'monospace', fontSize: '7.5px' }}>
              {isBlank ? '______' : val(orden?.id)}
            </span>
          </div>
          <div>
            <span style={S.label}>Cliente corporativo</span>
            <span style={S.value}>{isBlank ? '______' : val(orden?.cliente_corporativo || (modoB2B ? 'B2B' : '—'))}</span>
          </div>
          <div>
            <span style={S.label}>Caso externo</span>
            <span style={{ ...S.value, fontFamily: 'monospace' }}>
              {isBlank ? '______' : val(orden?.numero_autorizacion || orden?.caso_externo)}
            </span>
          </div>
          <div>
            <span style={S.label}>Creado por</span>
            <span style={S.value}>{isBlank ? '______' : getCreador()}</span>
          </div>
        </div>

        <div style={S.hrDark} />

        {/* BLOQUE 2: TÉCNICO — Izq: Avería+Diagnóstico / Der: RI+QC+Log+CPI */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8mm', marginBottom: '3mm' }}>

          {/* Columna izquierda */}
          <div>
            <span style={S.label}>Avería reportada</span>
            <p style={{ margin: '0 0 3mm', fontSize: '8px', lineHeight: '1.5', whiteSpace: 'pre-wrap', maxHeight: '17mm', overflow: 'hidden' }}>
              {isBlank
                ? '________________________________\n________________________________\n________________________________'
                : (orden?.dispositivo?.daños || orden?.averia_descripcion || 'Sin descripción')}
            </p>

            <span style={S.label}>Diagnóstico técnico</span>
            <p style={{
              margin: 0,
              fontSize: '8px',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap',
              maxHeight: '22mm',
              overflow: 'hidden',
              color: (isBlank || orden?.diagnostico_tecnico) ? '#111' : '#999',
              fontStyle: (!isBlank && !orden?.diagnostico_tecnico) ? 'italic' : 'normal',
            }}>
              {isBlank
                ? '________________________________\n________________________________\n________________________________\n________________________________'
                : (orden?.diagnostico_tecnico || 'Pendiente de diagnóstico')}
            </p>
            {orden?.tecnico_asignado && !isBlank && (
              <p style={{ margin: '1mm 0 0', fontSize: '7.5px', color: '#666' }}>— {getTecnicoNombre()}</p>
            )}
          </div>

          {/* Columna derecha */}
          <div>
            {/* RI */}
            <span style={S.label}>Inspección de entrada (RI)</span>
            <div style={{ fontSize: '7.5px', marginBottom: '2.5mm', lineHeight: '1.5' }}>
              <p style={{ margin: 0 }}>
                Resultado: <strong>{isBlank ? '_____' : val(orden?.ri_resultado, 'PENDIENTE')}</strong>
                {' '}· Completada: {boolLabel(orden?.ri_completada)}
              </p>
              <p style={{ margin: 0 }}>
                Checklist: {boolLabel(orden?.recepcion_checklist_completo)}
                {' '}· Físico: {boolLabel(orden?.recepcion_estado_fisico_registrado)}
                {' '}· Accesorios: {boolLabel(orden?.recepcion_accesorios_registrados)}
              </p>
              <p style={{ margin: 0 }}>
                Resp.: {isBlank ? '_____' : safeName(orden?.ri_usuario_nombre || orden?.ri_usuario, 'Nombre no configurado')}
                {' '}· Evidencias: {isBlank ? '_____' : `${allFotos.length} foto(s)`}
              </p>
            </div>

            {/* QC - Control de Calidad de Diagnóstico de Salida
                Se oculta cuando NSYS ya auto-verificó la OT para no duplicar info en el PDF. */}
            {!orden?.qc_verificado_nsys && (
              <>
                <span style={S.label}>Control de calidad final (QC) - Diagnóstico de Salida</span>
                <div style={{ fontSize: '7.5px', marginBottom: '2.5mm', lineHeight: '1.5', padding: '2mm', backgroundColor: orden?.diagnostico_salida_realizado && orden?.funciones_verificadas ? '#f0fdf4' : '#fef2f2', border: '1px solid', borderColor: orden?.diagnostico_salida_realizado && orden?.funciones_verificadas ? '#22c55e' : '#ef4444', borderRadius: '2mm' }}>
              {/* Resultado QC final marcado por el técnico (siempre visible si tiene valor) */}
              {!isBlank && orden?.qc_resultado_averia && (() => {
                const res = orden.qc_resultado_averia;
                const meta =
                  res === 'reparada'    ? { label: 'REPARADA',  bg: '#dcfce7', color: '#166534', border: '#16a34a', icon: '✅' } :
                  res === 'parcial'     ? { label: 'PARCIALMENTE REPARADA', bg: '#fef3c7', color: '#92400e', border: '#f59e0b', icon: '⚠️' } :
                  res === 'no_reparada' ? { label: 'NO REPARADA / IRREPARABLE', bg: '#fee2e2', color: '#991b1b', border: '#ef4444', icon: '❌' } :
                  null;
                if (!meta) return null;
                return (
                  <div style={{
                    marginBottom: '2mm',
                    padding: '1.5mm 2mm',
                    backgroundColor: meta.bg,
                    border: `1px solid ${meta.border}`,
                    borderRadius: '1.5mm',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{ fontSize: '6.5px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      Resultado del QC del técnico
                    </span>
                    <strong style={{ fontSize: '8.5px', color: meta.color, letterSpacing: '0.3px' }}>
                      {meta.icon} {meta.label}
                    </strong>
                  </div>
                );
              })()}

              <p style={{ margin: 0, fontWeight: '600', fontSize: '8px' }}>
                ✓ Estado del Dispositivo: <strong style={{ color: (orden?.diagnostico_salida_realizado && orden?.funciones_verificadas && orden?.limpieza_realizada) ? '#16a34a' : '#dc2626' }}>
                  {isBlank
                    ? '_____'
                    : ((orden?.diagnostico_salida_realizado && orden?.funciones_verificadas && orden?.limpieza_realizada) ? '✅ VERIFICADO - CONFORME' : '⚠️ PENDIENTE DE VERIFICACIÓN')}
                </strong>
              </p>
              <div style={{ marginTop: '1.5mm', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2mm' }}>
                <p style={{ margin: 0 }}>
                  <span style={{ fontSize: '6px', color: '#666' }}>Diagnóstico salida:</span><br/>
                  <strong>{orden?.diagnostico_salida_realizado ? '✓ Realizado' : '✗ Pendiente'}</strong>
                </p>
                <p style={{ margin: 0 }}>
                  <span style={{ fontSize: '6px', color: '#666' }}>Funciones verificadas:</span><br/>
                  <strong>{orden?.funciones_verificadas ? '✓ OK' : '✗ Pendiente'}</strong>
                </p>
                <p style={{ margin: 0 }}>
                  <span style={{ fontSize: '6px', color: '#666' }}>Limpieza:</span><br/>
                  <strong>{orden?.limpieza_realizada ? '✓ Realizada' : '✗ Pendiente'}</strong>
                </p>
              </div>
              {/* Bloque de funciones del sistema con soporte completo */}
              {!isBlank && (() => {
                const noAplica = orden?.qc_funciones_no_aplica === true;
                const noEsSmartphone = orden?.qc_es_smartphone === false;
                const fns = orden?.qc_funciones || {};
                const labels = {
                  pantalla_touch: 'Pantalla/Touch',
                  wifi: 'WiFi',
                  bluetooth: 'Bluetooth',
                  camara_trasera: 'Cám. trasera',
                  camara_frontal: 'Cám. frontal',
                  microfono: 'Micrófono',
                  altavoz_auricular: 'Altavoz',
                  carga: 'Carga',
                  botones_fisicos: 'Botones',
                  sim_red: 'SIM/Red',
                  biometria: 'Biometría',
                };
                if (noEsSmartphone) {
                  return (
                    <div style={{ marginTop: '2mm', paddingTop: '1.5mm', borderTop: '1px dashed #ccc' }}>
                      <span style={{ fontSize: '6px', color: '#666' }}>Funciones del sistema:</span>
                      <p style={{ margin: '0.5mm 0 0 0', fontSize: '7px', fontStyle: 'italic', color: '#64748b' }}>
                        No aplica — el dispositivo no es smartphone/tablet (consola, TV u otro aparato).
                      </p>
                    </div>
                  );
                }
                if (noAplica) {
                  return (
                    <div style={{ marginTop: '2mm', paddingTop: '1.5mm', borderTop: '1px dashed #ccc' }}>
                      <span style={{ fontSize: '6px', color: '#666' }}>Funciones del sistema:</span>
                      <p style={{ margin: '0.5mm 0 0 0', fontSize: '7px', fontStyle: 'italic', color: '#64748b' }}>
                        Marcadas como <strong>no aplica</strong> por el técnico (dispositivo sin estas funciones).
                      </p>
                    </div>
                  );
                }
                const marcadas = Object.entries(fns).filter(([, v]) => v).map(([k]) => labels[k] || k);
                const noMarcadas = Object.entries(fns).filter(([, v]) => !v).map(([k]) => labels[k] || k);
                if (Object.keys(fns).length === 0) return null;
                return (
                  <div style={{ marginTop: '2mm', paddingTop: '1.5mm', borderTop: '1px dashed #ccc' }}>
                    <span style={{ fontSize: '6px', color: '#666' }}>Funciones del sistema verificadas:</span>
                    {marcadas.length > 0 && (
                      <p style={{ margin: '0.5mm 0 0 0', fontSize: '7px', color: '#16a34a' }}>
                        ✓ {marcadas.join(' · ')}
                      </p>
                    )}
                    {noMarcadas.length > 0 && (
                      <p style={{ margin: '0.5mm 0 0 0', fontSize: '7px', color: '#dc2626' }}>
                        ✗ Sin verificar: {noMarcadas.join(' · ')}
                      </p>
                    )}
                  </div>
                );
              })()}
              {orden?.bateria_estado && !isBlank && (
                <p style={{ margin: '1.5mm 0 0 0', fontSize: '7px' }}>
                  <span style={{ fontSize: '6px', color: '#666' }}>Batería:</span>{' '}
                  {orden.bateria_estado === 'ok' ? '✓ OK (>80%)' : 
                   orden.bateria_estado === 'degradada' ? '⚠️ Degradada (<80%)' :
                   orden.bateria_estado === 'reemplazada' ? '🔄 Reemplazada' : 'N/A'}
                  {orden.bateria_nivel ? ` · ${orden.bateria_nivel}%` : ''}
                  {orden.bateria_ciclos ? ` · ${orden.bateria_ciclos} ciclos` : ''}
                </p>
              )}
              <p style={{ margin: '1.5mm 0 0 0' }}>
                <span style={{ fontSize: '6px', color: '#666' }}>Técnico responsable:</span>{' '}
                {isBlank ? '_____' : getTecnicoNombre()}
                {' '}·{' '}
                <span style={{ fontSize: '6px', color: '#666' }}>Fecha QC:</span>{' '}
                {isBlank ? '_____' : fmtDT(orden?.fecha_fin_reparacion || orden?.updated_at)}
              </p>
              {/* Notas de cierre técnico */}
              {orden?.notas_cierre_tecnico && !isBlank && (
                <div style={{ marginTop: '2mm', paddingTop: '1.5mm', borderTop: '1px dashed #ccc' }}>
                  <span style={{ fontSize: '6px', color: '#666' }}>Notas de cierre técnico:</span>
                  <p style={{ margin: '0.5mm 0 0 0', fontSize: '7px', fontStyle: 'italic' }}>
                    {orden.notas_cierre_tecnico}
                  </p>
                </div>
              )}
            </div>
              </>
            )}

            {/* Diagnóstico NSYS: extraído del grid 2 columnas y movido a ancho completo
                debajo del grid (ver bloque NSYS-FULLWIDTH). Esto evita la "tira larga"
                cuando hay 30+ tests. */}

            {/* Trazabilidad de Baterías */}
            {(orden?.bateria_reemplazada || orden?.bateria_estado || isBlank) && (
              <div style={{ marginBottom: '2.5mm' }}>
                <span style={S.label}>Trazabilidad de Baterías</span>
                <div style={{ fontSize: '7.5px', lineHeight: '1.5', padding: '2mm', backgroundColor: '#fefce8', border: '1px solid #eab308', borderRadius: '2mm' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm' }}>
                    <p style={{ margin: 0 }}>
                      <span style={{ fontSize: '6px', color: '#666' }}>Batería reemplazada:</span><br/>
                      <strong>{isBlank ? '_____' : (orden?.bateria_reemplazada ? '✓ Sí' : '✗ No')}</strong>
                    </p>
                    <p style={{ margin: 0 }}>
                      <span style={{ fontSize: '6px', color: '#666' }}>Almac. temporal:</span><br/>
                      <strong>{isBlank ? '_____' : (orden?.bateria_almacenamiento_temporal ? '✓ Aplicado' : '—')}</strong>
                    </p>
                  </div>
                  {orden?.bateria_reemplazada && !isBlank && (
                    <div style={{ marginTop: '1.5mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm' }}>
                      <p style={{ margin: 0 }}>
                        <span style={{ fontSize: '6px', color: '#666' }}>Residuo pendiente:</span>{' '}
                        {orden?.bateria_residuo_pendiente ? '⚠️ Sí' : '✓ No'}
                      </p>
                      {orden?.bateria_gestor_autorizado && (
                        <p style={{ margin: 0 }}>
                          <span style={{ fontSize: '6px', color: '#666' }}>Gestor:</span>{' '}
                          {orden.bateria_gestor_autorizado}
                        </p>
                      )}
                    </div>
                  )}
                  {orden?.bateria_fecha_entrega_gestor && !isBlank && (
                    <p style={{ margin: '1mm 0 0 0', fontSize: '6.5px' }}>
                      <span style={{ color: '#666' }}>Fecha entrega gestor:</span>{' '}
                      {fmtDate(orden.bateria_fecha_entrega_gestor)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Logística */}
            {(orden?.agencia_envio || orden?.codigo_recogida_entrada || orden?.codigo_recogida_salida || isBlank) && (
              <div style={{ marginBottom: '2.5mm' }}>
                <span style={S.label}>Logística</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2mm', fontSize: '7.5px' }}>
                  <div>
                    <span style={{ ...S.label, fontSize: '6.5px' }}>Agencia</span>
                    <span>{val(orden?.agencia_envio)}</span>
                  </div>
                  <div>
                    <span style={{ ...S.label, fontSize: '6.5px' }}>Cód. Entrada</span>
                    <span style={{ fontFamily: 'monospace' }}>{val(orden?.codigo_recogida_entrada)}</span>
                  </div>
                  <div>
                    <span style={{ ...S.label, fontSize: '6.5px' }}>Cód. Salida</span>
                    <span style={{ fontFamily: 'monospace' }}>{val(orden?.codigo_recogida_salida)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* CPI / NIST */}
            <span style={S.label}>Privacidad / CPI-NIST 800-88</span>
            <div style={{ fontSize: '7.5px', lineHeight: '1.5' }}>
              {(() => {
                const opcionLabels = {
                  cliente_ya_restablecio:       'Ya venía restablecido por el cliente (verificado)',
                  cliente_no_autoriza:           'Cliente NO autoriza restablecer/borrar (Privacidad alta)',
                  sat_realizo_restablecimiento:  'Restablecimiento realizado por el SAT',
                };
                const opcionLabel = orden?.cpi_opcion
                  ? (opcionLabels[orden.cpi_opcion] || orden.cpi_opcion)
                  : (orden?.cpi_resultado ? '(registro anterior)' : null);
                // Nombre del técnico: nunca mostrar email
                const nombreTecnico = safeName(orden?.cpi_usuario_nombre || orden?.cpi_usuario, 'Nombre no configurado');
                return (
                  <>
                    <p style={{ margin: 0, fontWeight: '500' }}>
                      {isBlank ? '_____' : (opcionLabel || 'PENDIENTE')}
                    </p>
                    {orden?.cpi_opcion === 'sat_realizo_restablecimiento' && !isBlank && (
                      <p style={{ margin: 0 }}>Método: {val(orden?.cpi_metodo)}</p>
                    )}
                    <p style={{ margin: 0 }}>
                      Resp.: {isBlank ? '_____' : nombreTecnico}
                      {' '}· Fecha: {isBlank ? '_____' : (orden?.cpi_fecha ? fmtDT(orden.cpi_fecha) : 'PENDIENTE')}
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ====== NSYS-FULLWIDTH: Diagnóstico NSYS a ancho completo ====== */}
        {/* Se renderiza después del grid 2-columnas para no quedar comprimido en
            una tira larga. La tabla de tests se pinta en 2 columnas para que
            ocupe menos vertical y no se parta entre páginas. */}
        {(orden?.nsys_data?.evaluation?.summary || orden?.nsys_parts_status?.result) && (() => {
          const summary = orden?.nsys_data?.evaluation?.summary || {};
          const reasons = orden?.nsys_data?.evaluation?.reasons || [];
          const verified = orden?.qc_verificado_nsys;
          const allTests = orden?.nsys_data?.evaluation?.all_tests || [];
          const partsResult = orden?.nsys_parts_status?.result;
          const partsList = Array.isArray(partsResult) ? partsResult : (partsResult?.Parts || partsResult?.parts || []);
          const partsNoOrig = Array.isArray(partsList) ? partsList.filter(p =>
            p.IsOriginal === false || p.Original === false || (typeof p.Status === 'string' && /no.*orig|non.*orig|repla/i.test(p.Status))
          ) : [];

          // Split de tests en 2 mitades para tabla en 2 columnas
          const half = Math.ceil(allTests.length / 2);
          const testsCol1 = allTests.slice(0, half);
          const testsCol2 = allTests.slice(half);
          const TestsTable = ({ rows }) => (
            <table style={{ width: '100%', fontSize: '6.5px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#e2e8f0' }}>
                  <th style={{ padding: '0.6mm', textAlign: 'left', border: '0.3mm solid #cbd5e1' }}>Test</th>
                  <th style={{ padding: '0.6mm', textAlign: 'left', border: '0.3mm solid #cbd5e1' }}>Grupo</th>
                  <th style={{ padding: '0.6mm', textAlign: 'center', border: '0.3mm solid #cbd5e1', width: '8mm' }}>OK</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t, i) => (
                  <tr key={i} style={{ backgroundColor: t.value === 0 ? '#fee2e2' : '#f0fdf4', pageBreakInside: 'avoid' }}>
                    <td style={{ padding: '0.5mm', border: '0.3mm solid #cbd5e1' }}>{t.item}</td>
                    <td style={{ padding: '0.5mm', border: '0.3mm solid #cbd5e1', color: '#666' }}>{t.subgroup || t.group}</td>
                    <td style={{ padding: '0.5mm', border: '0.3mm solid #cbd5e1', textAlign: 'center', fontWeight: 'bold', color: t.value === 0 ? '#dc2626' : '#16a34a' }}>
                      {t.value === 1 ? '✓' : '✗'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );

          return (
            <div style={{ marginBottom: '3mm' }}>
              <span style={S.label}>Diagnóstico NSYS (automatizado)</span>
              <div style={{
                fontSize: '7.5px',
                lineHeight: '1.5',
                padding: '2mm',
                backgroundColor: verified ? '#f0fdf4' : '#fef3c7',
                border: '1px solid',
                borderColor: verified ? '#16a34a' : '#f59e0b',
                borderRadius: '2mm',
              }}>
                {/* Cabecera */}
                <div style={{
                  marginBottom: '2mm', padding: '1.2mm 2mm',
                  backgroundColor: verified ? '#dcfce7' : '#fef3c7',
                  border: `1px solid ${verified ? '#16a34a' : '#f59e0b'}`,
                  borderRadius: '1.5mm',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: '6.5px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    Verificación NSYS
                  </span>
                  <strong style={{ fontSize: '8.5px', color: verified ? '#166534' : '#92400e' }}>
                    {verified ? '✅ AUTO-VERIFICADO' : '⚠️ REQUIERE REVISIÓN'}
                  </strong>
                </div>

                {/* Grid 7 columnas: Grado · Batería · Ciclos · Capacidad · ESN · SIM · FMIP */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2mm', marginBottom: '2mm', padding: '1.5mm', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '1.5mm' }}>
                  <div>
                    <span style={{ fontSize: '6px', color: '#666', display: 'block' }}>Grado</span>
                    <strong style={{ fontSize: '11px' }}>{summary.grade || '—'}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '6px', color: '#666', display: 'block' }}>Batería salud</span>
                    <strong style={{ color: Number(summary.battery_health) >= 80 ? '#16a34a' : '#dc2626' }}>
                      {summary.battery_health ? `${summary.battery_health}%` : '—'}
                    </strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '6px', color: '#666', display: 'block' }}>Ciclos</span>
                    <strong>{summary.battery_cycles ?? '—'}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '6px', color: '#666', display: 'block' }}>Capacidad</span>
                    <strong>{summary.battery_full_charge_capacity ? `${summary.battery_full_charge_capacity} mAh` : '—'}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '6px', color: '#666', display: 'block' }}>ESN</span>
                    <strong style={{ color: summary.esn === 'CLEAN' ? '#16a34a' : '#dc2626' }}>{summary.esn || '—'}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '6px', color: '#666', display: 'block' }}>SIM Lock</span>
                    <strong style={{ color: summary.lock_status === 'Unlocked' ? '#16a34a' : '#dc2626' }}>{summary.lock_status || '—'}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: '6px', color: '#666', display: 'block' }}>FMIP/FRP</span>
                    <strong style={{ color: !summary.fmip_frp || summary.fmip_frp === '0' ? '#16a34a' : '#dc2626' }}>{summary.fmip_frp || '—'}</strong>
                  </div>
                </div>

                {/* Resumen tests */}
                {summary.tests_total > 0 && (
                  <div style={{ marginBottom: '1.5mm', padding: '1.5mm', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '1.5mm', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    <span style={{ fontSize: '7.5px' }}><strong style={{ color: '#16a34a' }}>✓ {summary.tests_passed}</strong> pasados</span>
                    <span style={{ fontSize: '7.5px' }}><strong style={{ color: '#dc2626' }}>✗ {summary.tests_failed}</strong> fallidos</span>
                    <span style={{ fontSize: '7px', color: '#666' }}>{summary.tests_total} totales</span>
                  </div>
                )}

                {/* Tabla pass/fail en 2 columnas */}
                {allTests.length > 0 && (
                  <div style={{ marginBottom: '1.5mm' }}>
                    <span style={{ fontSize: '6px', color: '#666' }}>Detalle tests:</span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm', marginTop: '0.5mm' }}>
                      <TestsTable rows={testsCol1} />
                      <TestsTable rows={testsCol2} />
                    </div>
                  </div>
                )}

                {/* Piezas no originales */}
                {Array.isArray(partsList) && partsList.length > 0 && (
                  <div style={{ marginBottom: '1.5mm', padding: '1.5mm', backgroundColor: partsNoOrig.length > 0 ? '#fee2e2' : '#dcfce7', border: `1px solid ${partsNoOrig.length > 0 ? '#ef4444' : '#16a34a'}`, borderRadius: '1.5mm' }}>
                    <strong style={{ fontSize: '7px', color: partsNoOrig.length > 0 ? '#991b1b' : '#166534' }}>
                      {partsNoOrig.length > 0
                        ? `⚠️ ${partsNoOrig.length} pieza(s) NO original(es) detectada(s)`
                        : '✓ Todas las piezas son originales'}
                    </strong>
                    <ul style={{ margin: '0.5mm 0 0 3mm', padding: 0, fontSize: '6.5px' }}>
                      {partsList.map((p, i) => (
                        <li key={i}>
                          <span style={{ fontFamily: 'monospace' }}>{p.PartName || p.Part || p.Name || p.name}</span>{' · '}
                          <strong style={{ color: partsNoOrig.includes(p) ? '#dc2626' : '#16a34a' }}>
                            {p.Status || (p.IsOriginal === false ? 'NO ORIGINAL' : 'Original')}
                          </strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Motivos de revisión */}
                {!verified && reasons.length > 0 && (
                  <div style={{ marginBottom: '1.5mm', padding: '1mm', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '1mm' }}>
                    <strong style={{ fontSize: '6.5px', color: '#92400e' }}>Motivos de revisión:</strong>
                    <ul style={{ margin: '0.3mm 0 0 3mm', padding: 0, fontSize: '6.5px', color: '#92400e' }}>
                      {reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}

                {/* QR Certificado oficial */}
                {orden?.nsys_diagnostics_certificate?.link && (
                  <div style={{ marginTop: '1.5mm', padding: '1.5mm', backgroundColor: 'rgba(255,255,255,0.7)', border: '1px solid #0055FF', borderRadius: '1.5mm', display: 'flex', alignItems: 'center', gap: '2mm' }}>
                    <div style={{ background: 'white', padding: '0.5mm' }}>
                      <QRCode value={orden.nsys_diagnostics_certificate.link} size={64} style={{ height: '14mm', width: '14mm' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: '7.5px', color: '#1e40af' }}>📋 Certificado oficial de diagnóstico NSYS</strong>
                      <p style={{ margin: '0.5mm 0 0 0', fontSize: '6px', color: '#475569', wordBreak: 'break-all' }}>
                        Escanea el QR o accede a: <span style={{ fontFamily: 'monospace' }}>{orden.nsys_diagnostics_certificate.link.slice(0, 70)}...</span>
                      </p>
                      <p style={{ margin: '0.5mm 0 0 0', fontSize: '5.5px', color: '#64748b' }}>
                        IMEI: {orden?.nsys_diagnostics_certificate?.imei || '—'} · SN: {orden?.nsys_diagnostics_certificate?.serial || '—'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Pie con fecha de consulta */}
                {orden?.nsys_data?.fetched_at && (
                  <p style={{ margin: '1.5mm 0 0 0', fontSize: '5.5px', color: '#64748b', textAlign: 'right' }}>
                    Consultado en NSYS: {fmtDate(orden.nsys_data.fetched_at)}
                    {orden?.nsys_data?.fetched_by ? ` · por ${safeName(orden.nsys_data.fetched_by, '')}` : ''}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        <div style={S.hrDark} />

        {/* MATERIALES Y SERVICIOS */}
        <div style={{ marginBottom: '3mm' }}>
          <span style={{ ...S.label, marginBottom: '2mm', display: 'block' }}>Materiales y servicios</span>

          {materiales.length === 0 && !isBlank ? (
            <p style={{ color: '#999', fontStyle: 'italic', fontSize: '8px', margin: 0 }}>Sin materiales registrados</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '7.5px' }}>
              <thead>
                <tr style={{ borderBottom: '1.5px solid #ddd', backgroundColor: '#f8f8f8' }}>
                  <th style={{ textAlign: 'left', padding: '2mm 2mm 2mm 0', width: '70px', fontWeight: '700', fontSize: '7px', textTransform: 'uppercase', color: '#555' }}>SKU</th>
                  <th style={{ textAlign: 'left', padding: '2mm', fontWeight: '700', fontSize: '7px', textTransform: 'uppercase', color: '#555' }}>Descripción</th>
                  <th style={{ textAlign: 'left', padding: '2mm', width: '70px', fontWeight: '700', fontSize: '7px', textTransform: 'uppercase', color: '#555' }}>Proveedor</th>
                  <th style={{ textAlign: 'center', padding: '2mm', width: '35px', fontWeight: '700', fontSize: '7px', textTransform: 'uppercase', color: '#555' }}>Cant.</th>
                  {isFull && !isBlank && <th style={{ textAlign: 'right', padding: '2mm', width: '55px', fontWeight: '700', fontSize: '7px', textTransform: 'uppercase', color: '#555' }}>P. Unit.</th>}
                  {isFull && !isBlank && <th style={{ textAlign: 'right', padding: '2mm', width: '40px', fontWeight: '700', fontSize: '7px', textTransform: 'uppercase', color: '#555' }}>Dto.</th>}
                  {isFull && !isBlank && <th style={{ textAlign: 'right', padding: '2mm 0 2mm 2mm', width: '55px', fontWeight: '700', fontSize: '7px', textTransform: 'uppercase', color: '#555' }}>Subtotal</th>}
                </tr>
              </thead>
              <tbody>
                {(isBlank ? [{}, {}, {}] : materiales).map((m, i) => {
                  const st = (m.cantidad || 0) * (m.precio_unitario || 0);
                  const dto = m.descuento ? st * m.descuento / 100 : 0;
                  const isEven = i % 2 === 0;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #eee', backgroundColor: isEven ? '#fff' : '#fafafa' }}>
                      <td style={{ 
                        padding: '2.5mm 2mm 2.5mm 0', 
                        fontFamily: 'monospace', 
                        fontSize: '7px',
                        fontWeight: '600',
                        color: '#0055FF',
                        verticalAlign: 'top'
                      }}>
                        {isBlank ? '________' : (m.sku || '—')}
                      </td>
                      <td style={{ 
                        padding: '2.5mm 2mm', 
                        fontSize: '8px',
                        lineHeight: '1.3',
                        verticalAlign: 'top'
                      }}>
                        {isBlank ? '________________________' : (m.nombre || '—')}
                        {m.tipo && !isBlank && (
                          <span style={{ 
                            display: 'inline-block', 
                            marginLeft: '3mm', 
                            fontSize: '6px', 
                            padding: '0.5mm 1.5mm', 
                            backgroundColor: m.tipo === 'original' ? '#dcfce7' : m.tipo === 'compatible' ? '#fef3c7' : '#f3f4f6',
                            color: m.tipo === 'original' ? '#166534' : m.tipo === 'compatible' ? '#92400e' : '#374151',
                            borderRadius: '1mm',
                            fontWeight: '500',
                            textTransform: 'uppercase'
                          }}>
                            {m.tipo}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '2.5mm 2mm', fontSize: '7px', color: '#666', verticalAlign: 'top' }}>
                        {isBlank ? '________' : (m.proveedor || '—')}
                      </td>
                      <td style={{ textAlign: 'center', padding: '2.5mm 2mm', fontWeight: '600', verticalAlign: 'top' }}>
                        {isBlank ? '__' : (m.cantidad || 0)}
                      </td>
                      {isFull && !isBlank && (
                        <td style={{ textAlign: 'right', padding: '2.5mm 2mm', fontFamily: 'monospace', fontSize: '7px', verticalAlign: 'top' }}>
                          {(m.precio_unitario || 0).toFixed(2)} €
                        </td>
                      )}
                      {isFull && !isBlank && (
                        <td style={{ textAlign: 'right', padding: '2.5mm 2mm', color: m.descuento ? '#dc2626' : '#ccc', fontSize: '7px', verticalAlign: 'top' }}>
                          {m.descuento ? `-${m.descuento}%` : '—'}
                        </td>
                      )}
                      {isFull && !isBlank && (
                        <td style={{ textAlign: 'right', padding: '2.5mm 0 2.5mm 2mm', fontWeight: '600', fontFamily: 'monospace', fontSize: '7.5px', verticalAlign: 'top' }}>
                          {(st - dto).toFixed(2)} €
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Totales — solo modo full */}
          {isFull && !isBlank && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '3mm' }}>
              <div style={{ width: '160px', fontSize: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.8mm 0' }}>
                  <span style={{ color: '#666' }}>Base imponible</span>
                  <span>{totales.base.toFixed(2)} €</span>
                </div>
                {totales.iva > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.8mm 0' }}>
                    <span style={{ color: '#666' }}>
                      IVA {totales.ivaPct != null ? `${totales.ivaPct}%` : '(varios tipos)'}
                    </span>
                    <span>{totales.iva.toFixed(2)} €</span>
                  </div>
                )}
                {totales.iva === 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.8mm 0' }}>
                    <span style={{ color: '#666', fontStyle: 'italic' }}>Operación exenta de IVA</span>
                    <span>—</span>
                  </div>
                )}
                {(orden?.coste_transporte || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.8mm 0' }}>
                    <span style={{ color: '#666' }}>Transporte</span>
                    <span>{(orden.coste_transporte).toFixed(2)} €</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '1.5mm 0',
                  fontWeight: '800',
                  fontSize: '11px',
                  borderTop: '2px solid #111',
                  marginTop: '1.5mm',
                }}>
                  <span>Total</span>
                  <span>{(totales.total + (orden?.coste_transporte || 0)).toFixed(2)} €</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={S.hrLight} />

        {/* AVISO AVERÍA NO REPARADA / IRREPARABLE (si aplica) */}
        {!isBlank && (orden?.qc_resultado_averia === 'no_reparada' || orden?.estado === 'irreparable') && (
          <div style={{
            marginBottom: '4mm',
            padding: '3mm',
            backgroundColor: '#fef2f2',
            border: '2px solid #dc2626',
            borderRadius: '2mm'
          }}>
            <p style={{
              margin: '0 0 1.5mm',
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#dc2626',
              textTransform: 'uppercase'
            }}>
              ❌ AVERÍA NO REPARADA — Dispositivo IRREPARABLE
            </p>
            {orden?.qc_motivo_no_reparada && (
              <p style={{ margin: '0 0 1mm', fontSize: '8px', fontWeight: '500' }}>
                Motivo: {orden.qc_motivo_no_reparada}
              </p>
            )}
            <p style={{ margin: 0, fontSize: '7px', color: '#666' }}>
              Tras el diagnóstico técnico no ha sido posible reparar la avería. El dispositivo
              se devuelve al cliente sin intervención efectuada o se procede a la gestión del residuo
              según indicaciones del cliente.
            </p>
          </div>
        )}

        {/* AVISO REPARACIÓN PARCIAL (si aplica) */}
        {!isBlank && orden?.qc_resultado_averia === 'parcial' && (
          <div style={{
            marginBottom: '3mm',
            padding: '2.5mm',
            backgroundColor: '#fffbeb',
            border: '1.5px solid #f59e0b',
            borderRadius: '2mm'
          }}>
            <p style={{
              margin: '0 0 1mm',
              fontSize: '9px',
              fontWeight: 'bold',
              color: '#b45309',
              textTransform: 'uppercase'
            }}>
              ⚠️ Reparación PARCIAL
            </p>
            <p style={{ margin: 0, fontSize: '7.5px', color: '#78350f' }}>
              El dispositivo ha sido reparado parcialmente. Algunas funciones pueden no operar al
              100%. Consulta las notas de cierre técnico arriba para más detalle.
            </p>
          </div>
        )}

        {/* AVISO GARANTÍA NO PROCEDE (si aplica) */}
        {!isBlank && orden?.garantia_resultado === 'no_procede' && (
          <div style={{ 
            marginBottom: '4mm', 
            padding: '3mm', 
            backgroundColor: '#fef2f2', 
            border: '2px solid #dc2626',
            borderRadius: '2mm'
          }}>
            <p style={{ 
              margin: '0 0 1.5mm', 
              fontSize: '10px', 
              fontWeight: 'bold', 
              color: '#dc2626',
              textTransform: 'uppercase'
            }}>
              ⚠️ GARANTÍA NO PROCEDE
            </p>
            <p style={{ margin: '0 0 1mm', fontSize: '8px', fontWeight: '500' }}>
              Motivo: {orden?.garantia_motivo_no_procede || 'No especificado'}
            </p>
            <p style={{ margin: 0, fontSize: '7px', color: '#666' }}>
              La avería detectada no está cubierta por la garantía del servicio anterior.
              {orden?.numero_orden_padre && ` Orden original: ${orden.numero_orden_padre}`}
            </p>
          </div>
        )}

        {/* INDICACIONES GARANTÍA (si es orden de garantía) — dos bloques separados */}
        {orden?.es_garantia && (orden?.indicaciones_garantia_cliente || orden?.indicaciones_admin_garantia) && !isBlank && (
          <div style={{ marginBottom: '3mm', display: 'grid', gridTemplateColumns: orden?.indicaciones_admin_garantia ? '1fr 1fr' : '1fr', gap: '2mm' }}>
            {orden?.indicaciones_garantia_cliente && (
              <div style={{
                padding: '2mm',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '2mm'
              }}>
                <p style={{ margin: 0, fontSize: '7px', fontWeight: '600', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  Avería reportada por el cliente (Garantía)
                </p>
                <p style={{ margin: '1mm 0 0', fontSize: '8px' }}>
                  {orden.indicaciones_garantia_cliente}
                </p>
              </div>
            )}
            {orden?.indicaciones_admin_garantia && (
              <div style={{
                padding: '2mm',
                backgroundColor: '#dbeafe',
                border: '1px solid #3b82f6',
                borderRadius: '2mm'
              }}>
                <p style={{ margin: 0, fontSize: '7px', fontWeight: '600', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  Observaciones del admin (recepción)
                </p>
                <p style={{ margin: '1mm 0 0', fontSize: '8px' }}>
                  {orden.indicaciones_admin_garantia}
                </p>
              </div>
            )}
          </div>
        )}

        {/* GARANTÍA + NOTAS + QR */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '6mm', marginBottom: '3mm' }}>
          <div>
            <span style={S.label}>Garantía</span>
            <p style={{ margin: 0, fontSize: '8px' }}>
              {orden?.garantia_meses ? `${orden.garantia_meses} meses` : '6 meses'} desde la entrega
            </p>
            <p style={{ margin: '1mm 0 0', fontSize: '6.5px', color: '#777', lineHeight: '1.4' }}>
              {(empresa?.textos_legales?.clausulas_documentos || '').substring(0, 200) ||
               'Garantía válida para la avería reparada. Excluye daños por mal uso, golpes, líquidos o manipulación por terceros.'}
            </p>
          </div>

          {(orden?.notas || isBlank) ? (
            <div>
              <span style={S.label}>Notas</span>
              <p style={{ margin: 0, fontSize: '8px', maxHeight: '15mm', overflow: 'hidden' }}>
                {isBlank ? '________________________\n________________________' : (orden.notas || '—')}
              </p>
            </div>
          ) : <div />}

          <div style={{ textAlign: 'center' }}>
            <span style={{ ...S.label, display: 'block', textAlign: 'center' }}>Código de Identificación</span>
            {!isBlank ? (
              <BarcodeImage 
                value={orden?.numero_autorizacion || orden?.numero_orden || orden?.token_seguimiento || orden?.id?.substring(0, 12)} 
                width={1.2}
                height={35}
              />
            ) : (
              <div style={{ 
                width: '45mm', 
                height: '15mm', 
                border: '1px dashed #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <span style={{ fontSize: '7px', color: '#999' }}>Código de barras</span>
              </div>
            )}
          </div>
        </div>

        <div style={S.hrLight} />

        {/* TEXTO LEGAL */}
        <p style={{ fontSize: '6px', color: '#bbb', lineHeight: '1.4', margin: '0 0 3mm' }}>
          {empresa?.textos_legales?.politica_privacidad
            ? empresa.textos_legales.politica_privacidad
                .replace('[NOMBRE_EMPRESA]', empresa?.nombre || 'Mi Empresa')
                .substring(0, 350) + '...'
            : `En cumplimiento del RGPD, sus datos serán tratados por ${empresa?.nombre || 'Mi Empresa'} para gestionar este servicio técnico. Puede ejercer sus derechos en ${empresa?.email || 'info@empresa.es'}.`}
        </p>

        {/* FIRMAS */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20mm' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ ...S.label, marginBottom: '12mm' }}>Conforme del cliente</p>
            <div style={{ borderTop: '1px solid #ccc', paddingTop: '1.5mm' }}>
              <p style={{ fontSize: '6.5px', color: '#aaa', margin: 0 }}>Firma y DNI</p>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ ...S.label, marginBottom: '12mm' }}>Sello empresa</p>
            <div style={{ borderTop: '1px solid #ccc', paddingTop: '1.5mm' }}>
              <p style={{ fontSize: '6.5px', color: '#aaa', margin: 0 }}>{empresa?.nombre || ''}</p>
            </div>
          </div>
        </div>

        {/* PIE DE PÁGINA */}
        <div style={{ marginTop: '5mm', textAlign: 'center', fontSize: '6.5px', color: '#bbb' }}>
          <p style={{ margin: 0 }}>
            {empresa?.nombre} · {empresa?.web || empresa?.email || ''} · {empresa?.telefono || ''}
          </p>
          <p style={{ margin: '0.8mm 0 0', fontSize: '6px' }}>
            OT-PDF v2.0 · Modo: {mode}
            {includeFotos && photoPages.length > 0 ? ` · ${allFotos.length} foto(s) en anexo` : ''}
            {' '}· Pág. 1/{totalPages}
            {' '}· Generado: {isBlank ? '__/__/____' : new Date().toLocaleString('es-ES')}
          </p>
        </div>
      </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          PÁGINAS DE ANEXO FOTOGRÁFICO
      ══════════════════════════════════════════════════════════ */}
      {!onlyMain && photoPages.map((page, pageIdx) => (
        <div key={pageIdx} style={{ ...S.page, pageBreakBefore: 'always', breakBefore: 'page' }}>

          {/* Cabecera anexo */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm' }}>
            <div>
              <p style={{ fontSize: '16px', fontWeight: '800', margin: 0, letterSpacing: '-0.3px' }}>
                ANEXO FOTOGRÁFICO · {page.fase.toUpperCase()}
              </p>
              <p style={{ fontSize: '8px', color: '#666', margin: '1mm 0 0' }}>
                Orden: {orden?.numero_orden || '—'} · Dispositivo: {orden?.dispositivo?.modelo || '—'} · IMEI: {orden?.dispositivo?.imei || '—'}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: '700', margin: 0 }}>
                {orden?.numero_orden || '—'}
              </p>
              <p style={{ fontSize: '7.5px', color: '#999', margin: '0.5mm 0 0' }}>
                Pág. {pageIdx + 2}/{totalPages}
                {page.totalFase > 1 ? ` · ${page.fase} ${page.paginaFase}/${page.totalFase}` : ''}
              </p>
            </div>
          </div>

          {/* Banda de fase para identificar visualmente la sección */}
          <div style={{
            marginTop: '2mm',
            padding: '2mm 3mm',
            backgroundColor: '#f3f4f6',
            borderLeft: '3px solid #2563eb',
            borderRadius: '1mm',
          }}>
            <p style={{ fontSize: '8px', fontWeight: '700', color: '#1e40af', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {page.fase}
            </p>
            <p style={{ fontSize: '7px', color: '#475569', margin: '0.5mm 0 0' }}>
              {page.fotos.length} foto(s) en esta página
            </p>
          </div>

          <div style={S.hrDark} />

          {/* Grid 3 × 2 de fotos (6 por página). Antes era 4×2 (8) y dejaba
              huecos vacíos cuando la última página tenía menos fotos. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm', marginTop: '4mm' }}>
            {page.fotos.map((foto, fIdx) => {
              const globalIdx = (page.paginaFase - 1) * 6 + fIdx + 1;
              return (
                <div key={fIdx} style={{ display: 'flex', flexDirection: 'column' }}>
                  <PdfPhotoCell url={foto.url} alt={`Foto ${globalIdx}`} />
                  <div style={{ marginTop: '1mm', fontSize: '6.5px', color: '#666', textAlign: 'center' }}>
                    <strong style={{ color: '#111' }}>#{globalIdx}</strong> · {foto.tipo}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pie de página anexo */}
          <div style={{ marginTop: '6mm', textAlign: 'center', fontSize: '6.5px', color: '#bbb' }}>
            <p style={{ margin: 0 }}>
              {empresa?.nombre || 'Revix'} · Generado: {new Date().toLocaleString('es-ES')} · OT-PDF v2.0
            </p>
          </div>
        </div>
      ))}

      {/* ══════════════════════════════════════════════════════════
          PÁGINA FINAL · CONDICIONES GENERALES DEL SERVICIO TÉCNICO
          (siempre presente en todos los modos, salvo cuando solo
          renderizamos las fotos como bloque suelto o cuando el
          contenedor padre las añadirá al final por su cuenta)
      ══════════════════════════════════════════════════════════ */}
      {!onlyFotos && !skipCondiciones && (
        <CondicionesGeneralesPDF orden={orden} empresa={empresa} />
      )}
    </div>
  );
});

export default OrdenPDF;
